// src/lib/theme.js
//
// Merq Vault design system — locked tokens pulled from the redesign mockups.
// Keeps the suite palette consistent with getmerq.com landing.
//
// Import { theme, atoms, ...helpers } from '../lib/theme'

export const theme = {
  // ─── Surfaces ───────────────────────────────────────────
  colors: {
    cream:   '#FAF7F2',  // primary background
    cream2:  '#F5F1E8',  // sections / hover
    cream3:  '#EDE7DA',  // dividers, track backgrounds
    white:   '#FFFFFF',  // card surfaces

    // Sidebar chrome only
    navy:    '#0F172A',
    navy2:   '#111827',
    navy3:   '#1F2937',

    // Ink
    plum:      '#1E1040',  // primary text
    plumSoft:  '#443560',  // secondary
    inkMuted:  '#6B6480',  // tertiary
    inkQuiet:  '#9A93AB',  // hints / placeholders

    // Vault signature
    green:      '#16A34A',
    greenSoft:  '#DCF5E4',
    greenMint:  '#34D399',

    // States — used sparingly
    orange:      '#D97706',
    orangeSoft:  '#FEF3E2',
    rose:        '#BE185D',
    roseSoft:    '#FCE7F3',
    purple:      '#8B5CF6',
    purpleSoft:  '#EDE9FE',

    // Lines
    line:        'rgba(30, 16, 64, 0.08)',
    lineStrong:  'rgba(30, 16, 64, 0.14)',
  },

  // ─── Typography ──────────────────────────────────────────
  fonts: {
    display: "'Fraunces', Georgia, serif",
    body:    "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
  },

  // ─── Spacing scale ───────────────────────────────────────
  space: {
    xs: '4px', sm: '8px', md: '12px', lg: '16px',
    xl: '20px', '2xl': '24px', '3xl': '32px', '4xl': '40px',
    '5xl': '56px', '6xl': '80px',
  },

  // ─── Radii ───────────────────────────────────────────────
  radii: {
    sm: '8px',
    md: '10px',
    lg: '14px',
    xl: '22px',
    pill: '999px',
  },

  // ─── Shadows ─────────────────────────────────────────────
  shadows: {
    card:   '0 1px 2px rgba(30, 16, 64, 0.04), 0 1px 0 rgba(30, 16, 64, 0.03)',
    pop:    '0 6px 24px -8px rgba(30, 16, 64, 0.18)',
    fab:    '0 10px 30px -8px rgba(30, 16, 64, 0.4), 0 2px 6px rgba(30, 16, 64, 0.2)',
    fabHover:'0 14px 34px -8px rgba(30, 16, 64, 0.5), 0 2px 6px rgba(30, 16, 64, 0.2)',
    heroCard:'0 4px 16px -8px rgba(30, 16, 64, 0.08)',
  },

  // ─── Gradients ───────────────────────────────────────────
  gradients: {
    progress: 'linear-gradient(90deg, #16A34A 0%, #34D399 100%)',
    heroCard: 'linear-gradient(155deg, #FFFFFF 0%, #FAF7F2 100%)',
  },

  // ─── Breakpoints ─────────────────────────────────────────
  breakpoints: {
    mobile: 768,
    sidebar: 880,
  },
}

// ────────────────────────────────────────────────────────────
// Style atoms — reusable inline-style objects.
// Mix into component styles object with spread: { ...atoms.card, padding: '20px' }
// ────────────────────────────────────────────────────────────

const c = theme.colors
const f = theme.fonts
const r = theme.radii
const s = theme.shadows

export const atoms = {
  // ─── Buttons ────────────────────────────────────────────
  btnPrimary: {
    background: c.plum,
    color: c.cream,
    border: 'none',
    padding: '12px 22px',
    borderRadius: r.pill,
    fontFamily: f.body,
    fontWeight: 600,
    fontSize: '14.5px',
    cursor: 'pointer',
    transition: 'transform 140ms ease, box-shadow 140ms ease, background 140ms ease',
  },
  btnGreen: {
    background: c.green,
    color: '#FFFFFF',
    border: 'none',
    padding: '12px 22px',
    borderRadius: r.pill,
    fontFamily: f.body,
    fontWeight: 600,
    fontSize: '14.5px',
    cursor: 'pointer',
    transition: 'transform 140ms ease, box-shadow 140ms ease, background 140ms ease',
  },
  btnSecondary: {
    background: c.white,
    color: c.plum,
    border: `1px solid ${c.lineStrong}`,
    padding: '11px 20px',
    borderRadius: r.pill,
    fontFamily: f.body,
    fontWeight: 600,
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'background 120ms ease, border-color 120ms ease',
  },
  btnGhost: {
    background: 'transparent',
    color: c.plumSoft,
    border: `1px solid ${c.line}`,
    padding: '8px 14px',
    borderRadius: r.sm,
    fontFamily: f.body,
    fontWeight: 500,
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'background 120ms ease, border-color 120ms ease',
  },
  btnDanger: {
    background: c.rose,
    color: '#FFFFFF',
    border: 'none',
    padding: '11px 20px',
    borderRadius: r.pill,
    fontFamily: f.body,
    fontWeight: 600,
    fontSize: '14px',
    cursor: 'pointer',
  },

  // ─── Cards ──────────────────────────────────────────────
  card: {
    background: c.white,
    border: `1px solid ${c.line}`,
    borderRadius: r.lg,
    boxShadow: s.card,
    overflow: 'hidden',
  },
  cardHover: {
    background: c.white,
    border: `1px solid ${c.line}`,
    borderRadius: r.lg,
    boxShadow: s.card,
    overflow: 'hidden',
    transition: 'box-shadow 140ms ease, transform 140ms ease',
    cursor: 'pointer',
  },
  cardCream: {
    background: c.cream2,
    border: `1px solid ${c.line}`,
    borderRadius: r.lg,
  },

  // ─── Inputs ─────────────────────────────────────────────
  input: {
    width: '100%',
    background: c.white,
    border: `1px solid ${c.lineStrong}`,
    borderRadius: r.md,
    padding: '11px 14px',
    fontFamily: f.body,
    fontSize: '14.5px',
    color: c.plum,
    transition: 'border-color 120ms ease, background 120ms ease',
  },
  inputAmount: {
    width: '100%',
    background: 'transparent',
    border: 'none',
    fontFamily: f.display,
    fontWeight: 600,
    fontSize: '40px',
    color: c.plum,
    letterSpacing: '-0.025em',
    textAlign: 'center',
    outline: 'none',
  },
  label: {
    fontSize: '12.5px',
    fontWeight: 600,
    color: c.inkMuted,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginBottom: '8px',
    display: 'block',
  },

  // ─── Pills (status indicators) ──────────────────────────
  pillGreen: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    background: c.greenSoft,
    color: c.green,
    borderRadius: r.pill,
    fontSize: '12.5px',
    fontWeight: 600,
  },
  pillOrange: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    background: c.orangeSoft,
    color: c.orange,
    borderRadius: r.pill,
    fontSize: '12.5px',
    fontWeight: 600,
  },
  pillPurple: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    background: c.purpleSoft,
    color: c.purple,
    borderRadius: r.pill,
    fontSize: '12.5px',
    fontWeight: 600,
  },
  pillRose: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    background: c.roseSoft,
    color: c.rose,
    borderRadius: r.pill,
    fontSize: '12.5px',
    fontWeight: 600,
  },

  // ─── Hero amount display ────────────────────────────────
  amountDisplay: {
    fontFamily: f.display,
    fontWeight: 600,
    fontSize: '88px',
    lineHeight: 1,
    letterSpacing: '-0.035em',
    color: c.plum,
    display: 'flex',
    alignItems: 'baseline',
    gap: '6px',
  },
  amountCurrency: {
    fontSize: '40px',
    fontWeight: 500,
    color: c.plumSoft,
    transform: 'translateY(-22px)',
    letterSpacing: 0,
  },
  amountCents: {
    fontSize: '34px',
    fontWeight: 500,
    color: c.plumSoft,
    transform: 'translateY(-22px)',
    letterSpacing: 0,
  },
  amountInline: {
    fontFamily: f.display,
    fontWeight: 600,
    color: c.plum,
    letterSpacing: '-0.02em',
  },

  // ─── Page chrome ────────────────────────────────────────
  pageTitle: {
    fontFamily: f.display,
    fontWeight: 600,
    fontSize: '28px',
    letterSpacing: '-0.025em',
    color: c.plum,
    marginBottom: '4px',
  },
  pageMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    color: c.inkMuted,
    fontSize: '14px',
    fontWeight: 500,
  },
  pageMetaDot: {
    width: '3px',
    height: '3px',
    borderRadius: '50%',
    background: c.inkQuiet,
    flexShrink: 0,
  },
  sharingPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    color: c.green,
    fontWeight: 600,
    fontSize: '13.5px',
  },
}

// ────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────

export const statusColor = (kind) => {
  switch (kind) {
    case 'over':    return c.orange
    case 'warn':    return c.orange
    case 'ok':      return c.green
    case 'success': return c.green
    case 'danger':  return c.rose
    case 'info':    return c.purple
    default:        return c.inkMuted
  }
}

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const MONTHS_LONG  = ['January','February','March','April','May','June','July','August','September','October','November','December']

export const formatShortDate = (date) => {
  const d = date instanceof Date ? date : new Date(date)
  if (isNaN(d.getTime())) return ''
  return `${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}`
}

export const formatMonthYear = (date) => {
  const d = date instanceof Date ? date : new Date(date)
  if (isNaN(d.getTime())) return ''
  return `${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}`
}

export const firstName = (fullName) => {
  if (!fullName || typeof fullName !== 'string') return ''
  return fullName.trim().split(/\s+/)[0] || ''
}

export const firstOfMonth = (year, month1based) => {
  // month1based: 1 = Jan
  return new Date(year, month1based - 1, 1)
}

export const lastOfMonth = (year, month1based) => {
  return new Date(year, month1based, 0)
}

export default theme
